import { ErrorMessage } from '@gc-bridge/components/ErrorMessage';
import { PageLoading } from '@gc-bridge/components/PageLoading';
import { useAsync } from '@gc-bridge/hooks/useAsync';
import { authService, configService } from '@gc-bridge/services';
import { DEFAULT_CONFIG_TTL, useConfigStore } from '@gc-bridge/stores/config';
import { useCurrentUserStore } from '@gc-bridge/stores/currentUser';
import { GcOptions } from '@gc-bridge/theme/gcOptions';
import { loadRemoteConfig } from '@gcportal/base';
import { createTheme, ThemeProvider } from '@mui/material';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@gc-bridge/components/OktaReact/Security';
import { PublicRouter } from 'apps/shell/src/app/PublicRouter';
import { Router } from 'apps/shell/src/app/Router';
import { useEffect } from 'react';

export function App() {
  const gcTheme = createTheme(GcOptions);
  const configTtl = useConfigStore(({ config }) =>
    // Convert config TTL from seconds to milliseconds if present
    config?.configTtlSeconds ? config.configTtlSeconds * 1000 : DEFAULT_CONFIG_TTL
  );

  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        const { config: newConfig, hasChanged } = await configService.loadConfig();

        if (!newConfig) {
          console.warn('No configuration found during periodic reload.');
        } else if (hasChanged) {
          await loadRemoteConfig(newConfig, (await authService.getProfile())?.groups);
        }
      } catch (error) {
        console.error('Error reloading config:', error);
      }
    }, configTtl);

    return () => clearInterval(intervalId);
  }, [configTtl]);

  const { setUser } = useCurrentUserStore();
  const { error: errorLoadingCurrentUser, loading: loadingCurrentUser } =
    useAsync(async () => {
      const currentUser = await authService.getProfile();

      setUser(currentUser);
    }, [setUser]);

  if (loadingCurrentUser) {
    return <PageLoading />;
  }

  if (errorLoadingCurrentUser) {
    return <ErrorMessage error={errorLoadingCurrentUser} />;
  }

  if (!authService.oktaAuth) {
    return (
      <ThemeProvider theme={gcTheme}>
        <PublicRouter />
      </ThemeProvider>
    );
  }

  return (
    <Security
      oktaAuth={authService.oktaAuth}
      restoreOriginalUri={async (_oktaAuth, originalUri) => {
        window.location.href = toRelativeUrl(originalUri || '/', window.location.origin);
      }}
    >
      <ThemeProvider theme={gcTheme}>
        <Router />
      </ThemeProvider>
    </Security>
  );
}
